import { useState } from 'react';
import {
  graphql,
  useFragment,
  useLazyLoadQuery,
  useMutation,
} from 'react-relay/hooks';
import { useAuthorableContentsFromRelay } from '@sparknz/journeys-common';
import { useSnackbar } from '@sparknz/spark-pattern-library';

import { shopCartFragment$key } from '__generated__/shopCartFragment.graphql';
import { useCartActionDeleteItemMutation } from '__generated__/useCartActionDeleteItemMutation.graphql';
import { useCartActionFragment$key } from '__generated__/useCartActionFragment.graphql';
import { useCartActionGetCartQuery } from '__generated__/useCartActionGetCartQuery.graphql';
import { useCartActionOverallStockQuery } from '__generated__/useCartActionOverallStockQuery.graphql';
import {
  CART_JOURNEY_ID,
  DELETE_FAILED_CONTENT_KEY,
  DELETE_ITEM_CONTENT_KEY,
} from 'journey/cart/constants/cartJourney.constant';
import { CartFragment } from 'shared/api/fragments/shopCart.fragment';
import { useCartCookie, useSegment } from 'shared/hooks';
import getShippableOfferIds from 'shared/utils/getCartOfferIds.utils';

export const useCartActionFragment = graphql`
  fragment useCartActionFragment on Query
  @argumentDefinitions(
    cartId: { type: "String!" }
    channel: { type: "CartChannel!" }
  ) {
    cartView(cartId: $cartId, channel: $channel) {
      cart {
        ...shopCartFragment @arguments(channel: $channel)
      }
      screen
      mandatoryAction
    }
    me {
      accountList {
        accountId
        accountNumber
        firstName
        lastName
        balanceManagement
        customerSegment
        segment
      }
    }
  }
`;

const cartQuery = graphql`
  query useCartActionGetCartQuery($cartId: String!, $channel: CartChannel!) {
    ...useCartActionFragment @arguments(cartId: $cartId, channel: $channel)
  }
`;

const cartOverallStockQuery = graphql`
  query useCartActionOverallStockQuery($offerIds: [String!]!) {
    onlineStock(offerIds: $offerIds) {
      stockStatus
    }
    storeStock(offerIds: $offerIds) {
      availability
    }
  }
`;

const deleteItemMutation = graphql`
  mutation useCartActionDeleteItemMutation(
    $input: DeleteItemFromBundleInput!
    $channel: CartChannel!
  ) {
    deleteItemFromBundle(input: $input) {
      message
      code
      success
      cart {
        ...shopCartFragment @arguments(channel: $channel)
      }
    }
  }
`;

interface CommonCartActionParams {
  onCompleted?: () => void;
  onError?: () => void;
}

export interface DeleteItemActionParams extends CommonCartActionParams {
  bundleId: string;
  itemId: string;
  productOfferingId: string;
}

const getUnixTimestampInSeconds = () => Math.floor(Date.now() / 1000);

export function useCartAction(defaultCartQueryFetchKey?: string) {
  const { cartId, numberOfCartItems } = useCartCookie();
  const { channel } = useSegment();
  const { getText } = useAuthorableContentsFromRelay(CART_JOURNEY_ID);
  const [cartQueryFetchKey, setCartQueryFetchKey] = useState<string | number>(
    defaultCartQueryFetchKey ?? 0,
  );

  const cartData = useLazyLoadQuery<useCartActionGetCartQuery>(
    cartQuery,
    {
      cartId,
      channel,
    },
    {
      fetchKey: cartQueryFetchKey,
      fetchPolicy: 'network-only',
    },
  );
  const {
    cartView,
    me: { accountList },
  } = useFragment<useCartActionFragment$key>(useCartActionFragment, cartData);

  const cart = useFragment<shopCartFragment$key>(CartFragment, cartView.cart);

  const shippableOfferIds = getShippableOfferIds(cart?.shippableItems);
  const { onlineStock, storeStock } =
    useLazyLoadQuery<useCartActionOverallStockQuery>(
      cartOverallStockQuery,
      {
        offerIds: shippableOfferIds,
      },
      {
        fetchPolicy: 'network-only',
      },
    );

  const stockInfo = {
    isInStockWarehouse: onlineStock?.stockStatus === 'IN_STOCK',
    isInStockStore: storeStock?.availability > 0,
  };

  const [commitDeleteItem, isDeletingItem] =
    useMutation<useCartActionDeleteItemMutation>(deleteItemMutation);

  const { showSnackbar } = useSnackbar();

  function refreshCartItems() {
    setCartQueryFetchKey(getUnixTimestampInSeconds());
  }

  const deleteItem = ({ bundleId, itemId }: DeleteItemActionParams) => {
    commitDeleteItem({
      variables: {
        input: {
          cartId,
          channel,
          bundleId,
          itemId,
        },
        channel,
      },
      onCompleted: () => {
        showSnackbar({
          iconName: 'cart_small',
          text: getText(DELETE_ITEM_CONTENT_KEY),
          variant: 'error',
        });
      },
      onError: () => {
        showSnackbar({
          text: getText(DELETE_FAILED_CONTENT_KEY),
        });
      },
    });
  };

  return {
    accountList,
    cart,
    cartId,
    cartView,
    deleteItem,
    isDeletingItem,
    numberOfCartItems,
    refreshCartItems,
    stockInfo,
  };
}
